import { useState, useEffect, useRef } from 'react';
import { X, Image as ImageIcon, Video, Smile, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { getCurrentUser } from '../utils/auth';
import { postAPI } from '../services/api';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import { useEmojiPickerPosition } from '../hooks/useEmojiPickerPosition';

const CreatePost = ({ onClose, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const { pickerRef, buttonRef, position } = useEmojiPickerPosition(showEmojiPicker);

  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        toast.error(`${file.name} is not a valid image or video file`);
        return false;
      }
      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 50MB)`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
    
    const previews = validFiles.map(file => ({
      url: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image'
    }));
    setPreviewUrls([...previewUrls, ...previews]);
  };

  const handleRemoveImage = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  const handleEmojiClick = (emojiObject) => {
    setContent(content + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && 
          pickerRef.current && 
          !pickerRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const handleSubmit = async () => {
    if (!content.trim() && selectedFiles.length === 0) {
      toast.error('Please add some content or media');
      return;
    }

    setUploading(true);

    try {
      // Upload media files first
      const mediaUrls = [];
      for (const file of selectedFiles) {
        console.log('Uploading file:', file.name, file.type, file.size);
        try {
          const response = await postAPI.uploadMedia(file);
          console.log('Upload response:', response.data);
          mediaUrls.push(response.data.mediaUrl);
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          throw new Error(`Failed to upload ${file.name}: ${uploadError.response?.data?.detail || uploadError.message}`);
        }
      }

      console.log('Creating post with content:', content, 'mediaUrls:', mediaUrls);
      
      // Create post
      const response = await postAPI.createPost(content, mediaUrls, []);
      console.log('Post created:', response.data);
      
      toast.success('Post created successfully!');
      if (onPostCreated) {
        onPostCreated(response.data.post);
      }
      onClose();
    } catch (error) {
      console.error('Error creating post:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMsg = error.message || error.response?.data?.detail || 'Failed to create post';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <h2 className="text-lg font-semibold text-foreground">Create Post</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="flex items-start space-x-3 mb-4">
            <Avatar className="w-10 h-10 ring-2 ring-purple-100">
              <AvatarImage src={currentUser.profileImage} alt={currentUser.name} />
              <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold">{currentUser.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-semibold text-sm text-foreground leading-tight">{currentUser.name}</p>
              <p className="text-xs text-muted-foreground">@{currentUser.username}</p>
            </div>
          </div>

          <Textarea
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-[120px] resize-none border-0 focus-visible:ring-0 text-[15px] text-foreground placeholder:text-muted-foreground bg-transparent px-0"
          />

          {/* Selected Media */}
          {previewUrls.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              {previewUrls.map((media, index) => (
                <div key={index} className="relative group">
                  {media.type === 'video' ? (
                    <video
                      src={media.url}
                      className="w-full h-48 object-cover rounded-xl"
                      controls
                    />
                  ) : (
                    <img
                      src={media.url}
                      alt={`Selected ${index + 1}`}
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  )}
                  <button
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/70 hover:bg-black rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border/50 px-5 py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <label className="cursor-pointer p-2 hover:bg-muted/50 rounded-full transition-colors" title="Upload Images">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <ImageIcon className="w-5 h-5 text-primary" />
              </label>
              <label className="cursor-pointer p-2 hover:bg-muted/50 rounded-full transition-colors" title="Upload Videos">
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <Video className="w-5 h-5 text-primary" />
              </label>
              <div className="relative">
                <button 
                  ref={buttonRef}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-2 hover:bg-muted/50 rounded-full transition-colors"
                >
                  <Smile className="w-5 h-5 text-primary" />
                </button>
                {showEmojiPicker && (
                  <div 
                    ref={pickerRef}
                    className="fixed z-50"
                    style={{
                      top: `${position.top}px`,
                      left: `${position.left}px`,
                    }}
                  >
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </div>
                )}
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              className="text-white bg-primary hover:bg-[#7C4EE6] rounded-full px-6 font-semibold"
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                'Post'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;
